import { Box, Button, Stack, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { FC } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  LoadingButton,
  ModalComponent,
  EConfirmModalType,
} from "src/shared/components";
import { modalText } from "src/shared/constants/form.constants";
import { routes } from "src/shared/constants/routes.constants";
import { isApiError, showApiError } from "src/shared/helpers/api.helpers";
import { useConfirmModal } from "src/shared/hooks";
import { notifyIntegration1C } from "../lib/NotifyIntegration1C";
import {
  usePostNdaScanMutation,
  usePostPartnerMutation,
  usePostLegalEntityCertificateMutation,
} from "../model/api/partners";
import { postPartnerFormDefaultValues } from "../model/constants";
import { getPostPartnerDTO } from "../model/helpers/helpers";
import { IPartnerFormValues } from "../model/types/partner";
import { Activity } from "./partnerForm/Activity";
import { BankDetails } from "./partnerForm/BankDetails";
import { ClientDescription } from "./partnerForm/ClientDescription";
import { Contacts } from "./partnerForm/Contacts";
import { GeneralInfo } from "./partnerForm/GeneralInfo";
import { RegistrationDetails } from "./partnerForm/RegistrationDetails";

export const NewPartner: FC = () => {
  const navigate = useNavigate();
  const [postPartner, { isLoading }] = usePostPartnerMutation();
  const [postNdaScan, { isLoading: isPostNdaScanLoading }] =
    usePostNdaScanMutation();
  const [
    postLegalEntityCertificate,
    { isLoading: postLegalEntityCertificateIsLoading },
  ] = usePostLegalEntityCertificateMutation();
  const [searchParams] = useSearchParams();
  const registration_country_id = searchParams.get("registration_country_id");
  const is_resident = searchParams.get("is_resident");
  const organization_form = searchParams.get("organization_form");
  const agency = searchParams.get("agency");
  const ownership_id = searchParams.get("ownership_id");

  const { modal, showModal } = useConfirmModal({
    onResolve: () => navigate(routes.partners),
    confirmModalType: EConfirmModalType.WARNING,
    confirmBtnProps: { isLoading: false, title: modalText.continue },
    children: <Typography>{modalText.unsavedChanges}</Typography>,
    title: modalText.confirmAction,
  });

  const methods = useForm<IPartnerFormValues>({
    defaultValues: {
      ...postPartnerFormDefaultValues,
      partner: {
        ...postPartnerFormDefaultValues.partner,

        registration_country_id: Number.isNaN(Number(registration_country_id))
          ? ""
          : Number(registration_country_id),
        is_resident: is_resident === "1" ? true : false,
        organization_form: Number.isNaN(Number(organization_form))
          ? ""
          : Number(organization_form),
        agency: Number.isNaN(Number(agency)) ? "" : Number(agency),
        ownership_id: Number.isNaN(Number(ownership_id))
          ? ""
          : Number(ownership_id),
      },
    },
    shouldFocusError: true,
    mode: "all",
  });

  const {
    handleSubmit,
    setError,
    formState: { isDirty },
  } = methods;
  const handleSave = async (values: IPartnerFormValues) => {
    try {
      const {
        nda_note,
        nda_file,
        is_nda_signed,
        state_registration_certificate,
        ...partnerData
      } = values.partner;

      const partnerResponse = await postPartner(
        getPostPartnerDTO({ ...values, partner: partnerData }),
      ).unwrap();

      const {
        data: { id },
      } = partnerResponse;

      const { integration_1c } = partnerResponse.data;

      if (is_nda_signed || nda_note) {
        try {
          await postNdaScan({
            partnerId: partnerResponse.data.id,
            file: nda_file!,
            note: nda_note!,
            is_nda_signed: !!is_nda_signed,
          }).unwrap();
        } catch (fileError) {
          showApiError(fileError);

          enqueueSnackbar("Не удалось прикрепить скан NDA. Попробуйте позже", {
            variant: "warning",
          });
        }
      }

      if (state_registration_certificate) {
        try {
          await postLegalEntityCertificate({
            partnerId: partnerResponse.data.id,
            file: state_registration_certificate!,
          }).unwrap();
        } catch (fileError) {
          showApiError(fileError);

          enqueueSnackbar(
            "Не удалось прикрепить Свидетельство ЮЛ. Попробуйте позже",
            {
              variant: "warning",
            },
          );
        }
      }

      notifyIntegration1C(enqueueSnackbar, integration_1c);

      navigate(`${routes.partners}/${id}`);
    } catch (e) {
      if (isApiError(e)) {
        if (e.data.errors["registration_number"]) {
          setError(
            "partner.registration_number",
            {
              message: e.data.errors["registration_number"].join("\r\n"),
              type: "custom",
            },
            { shouldFocus: true },
          );
        }
      }
      showApiError(e);
    }
  };

  return (
    <ModalComponent
      width={() => ({ xs: "100%", sm: "95vw", xxl: 1520 })}
      disableBodyPadding
      title="Новый партнер"
      onClose={isDirty ? showModal : () => navigate(routes.partners)}
      open
      modalFooter={
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={isDirty ? showModal : () => navigate(routes.partners)}
          >
            Отменить
          </Button>
          <LoadingButton
            isLoading={
              isLoading ||
              isPostNdaScanLoading ||
              postLegalEntityCertificateIsLoading
            }
            variant="contained"
            onClick={handleSubmit(handleSave)}
          >
            Сохранить
          </LoadingButton>
        </Box>
      }
    >
      {modal}
      <Box component="form" onSubmit={handleSubmit(handleSave)}>
        <FormProvider {...methods}>
          <Box
            sx={{
              backgroundColor: (theme) => theme.palette.grey[100],
              px: 4,
              py: 4,
            }}
          >
            <Stack direction="column" spacing={4}>
              <GeneralInfo />
              <RegistrationDetails />
              <BankDetails />
              <ClientDescription />
              <Contacts />
              <Activity />
            </Stack>
          </Box>
        </FormProvider>
      </Box>
    </ModalComponent>
  );
};
